<template>
  <div>
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center">
          <h3 class="mb-2">Dodajte prodavače</h3>
          <field-help-dialog
            title="Prodavači na prodajnom mjestu"
            description="Prodavači su korisnici koji će imati pristup blagajni i moći izdavati račune na ovom prodajnom mjestu. Svaki prodavač mora imati OIB i PIN kod za prijavu."
            :required="false"
            notes="OIB: Potreban je za identifikaciju prodavača na fiskalnom računu i evidenciji u Poreznoj upravi.

PIN KOD: 4-znamenkasti sigurnosni kod koji prodavač koristi za prijavu na blagajnu.

DODAVANJE PRODAVAČA: Možete odabrati postojeće korisnike iz firme ili dodati nove. Novi korisnici automatski dobivaju email pozivnicu za pristup sustavu.

OPCIONALNOST: Ovaj korak možete preskočiti i dodati prodavače kasnije kroz postavke firme. Međutim, za korištenje blagajne trebat će vam barem jedan prodavač."
          />
        </div>
        <p class="text--secondary mb-4">
          Odaberite korisnike koji će biti prodavači na prodajnom mjestu
        </p>
      </v-col>
    </v-row>

    <!-- Loading state -->
    <v-row v-if="loading">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary" />
        <p class="mt-2">Učitavam korisnike...</p>
      </v-col>
    </v-row>

    <!-- Nema korisnika -->
    <v-row v-else-if="users.length === 0">
      <v-col cols="12">
        <v-alert dense outlined type="info">
          Još nema korisnika u firmi. Dodajte prvog prodavača!
        </v-alert>
      </v-col>
    </v-row>

    <!-- Lista postojećih korisnika -->
    <v-row v-else>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="subtitle-2">Postojeći korisnici firme:</v-card-title>
          <v-list dense>
            <v-list-item
              v-for="user in users"
              :key="user.user.id"
            >
              <v-list-item-action v-if="hasOibAndPin(user)">
                <v-checkbox
                  :input-value="selectedUserIds.includes(user.user.id)"
                  @change="toggleUser(user.user.id)"
                  :disabled="isSuperadmin(user)"
                  color="primary"
                />
              </v-list-item-action>

              <v-list-item-icon v-if="hasOibAndPin(user)">
                <v-icon color="success">mdi-account-check</v-icon>
              </v-list-item-icon>
              <v-list-item-icon v-else>
                <v-icon color="warning">mdi-account-alert</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>
                  {{ user.user.name }} {{ user.user.surname }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="hasOibAndPin(user)">
                  OIB: {{ user.user.oib }} | PIN: {{ user.user.pincode }}
                  <span v-if="user.user.designation">| {{ user.user.designation }}</span>
                </v-list-item-subtitle>
                <v-list-item-subtitle v-else class="error--text">
                  OIB nije postavljen
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action v-if="!hasOibAndPin(user)">
                <v-btn
                  small
                  color="warning"
                  @click="openSetOibDialog(user)"
                >
                  Unesi OIB
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dugme za dodavanje novog -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-btn
          color="success"
          @click="openAddSellerDialog"
          :disabled="loading"
        >
          <v-icon left>mdi-account-plus</v-icon>
          Dodaj novog prodavača
        </v-btn>
      </v-col>
    </v-row>

    <!-- Info poruka -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-alert dense outlined text type="info" class="mb-0">
          <div style="line-height: 1.6;">
            <strong>Napomena:</strong> Ovaj korak je opcionalan. Možete dodati prodavače sada ili kasnije kroz postavke firme.
          </div>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Modali -->
    <set-oib-dialog
      v-model="setOibDialog"
      :user="currentUser"
      :company-id="companyId"
      :location-id="locationId"
      @oibSet="onOibSet"
    />

    <add-seller-dialog
      v-model="addSellerDialog"
      :company-id="companyId"
      :location-id="locationId"
      :domain="domain"
      @sellerAdded="onSellerAdded"
      @domainCreated="onDomainCreated"
    />
  </div>
</template>

<script>
import SetOibDialog from './SetOibDialog.vue'
import AddSellerDialog from './AddSellerDialog.vue'
import FieldHelpDialog from '../shared/FieldHelpDialog.vue'
import { df, functions, auth } from '@/plugins/firebase'

export default {
  name: 'StepSellers',
  components: {
    SetOibDialog,
    AddSellerDialog,
    FieldHelpDialog
  },
  inject: ['showMsgBox'],
  props: {
    companyId: {
      type: String,
      default: null
    },
    locationId: {
      type: String,
      default: null
    },
    domain: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      users: [],
      selectedUserIds: [],
      loading: false,
      setOibDialog: false,
      addSellerDialog: false,
      currentUser: null
    }
  },
  watch: {
    companyId (newVal) {
      if (newVal) {
        this.loadUsers()
      }
    }
  },
  methods: {
    async loadUsers (keepSelection = false) {
      // Ne učitavaj korisnike ako companyId nije postavljen
      if (!this.companyId) {
        this.users = []
        return
      }

      this.loading = true
      try {
        const snapshot = await df
          .collection(`companies/${this.companyId}/users`)
          .where('user.status', '==', 'OK')
          .get()

        this.users = snapshot.docs.map(doc => ({
          docId: doc.id,
          ...doc.data()
        }))

        // Sortiraj manualno jer nema index
        this.users.sort((a, b) => {
          const surnameA = a.user.surname || ''
          const surnameB = b.user.surname || ''
          return surnameA.localeCompare(surnameB)
        })

        // Ako keepSelection je true, zadrži postojeće selectedUserIds
        if (!keepSelection) {
          // Automatski dodaj korisnike koji su već na ovom prodajnom mjestu
          this.selectedUserIds = []
          const currentUserId = auth.currentUser.uid

          this.users.forEach(user => {
            // Provjeri da li korisnik ima OIB i PIN
            if (this.hasOibAndPin(user)) {
              const userLocations = user.user.location_ids || []
              // Dodaj ako je već na ovom prodajnom mjestu ili ako je trenutni korisnik (superadmin)
              if (userLocations.includes(this.locationId) || user.user.id === currentUserId) {
                this.selectedUserIds.push(user.user.id)
              }
            }
          })
        }
      } catch (error) {
        console.error('Error loading users:', error)
        this.users = []
      } finally {
        this.loading = false
      }
    },
    async toggleUser (userId) {
      const index = this.selectedUserIds.indexOf(userId)
      const userApi = functions.httpsCallable('userapi')

      try {
        if (index > -1) {
          // Ukloni korisnika sa prodajnog mjesta
          await userApi({
            action: {
              operation: 'delete',
              entity: 'resource',
              params: {
                user_ids: [userId],
                flag: 'del',
                company_id: this.companyId,
                location_ids: [this.locationId]
              }
            }
          })
          this.selectedUserIds.splice(index, 1)
        } else {
          // Dodaj korisnika na prodajno mjesto
          await userApi({
            action: {
              operation: 'set',
              entity: 'resource',
              params: {
                user_ids: [userId],
                flag: 'add',
                company_id: this.companyId,
                location_ids: [this.locationId]
              }
            }
          })
          this.selectedUserIds.push(userId)
        }
      } catch (error) {
        console.error('Error toggling user:', error)
        this.showMsgBox({
          text: 'Greška pri ažuriranju korisnika',
          color: 'error'
        })
      }
    },
    openSetOibDialog (user) {
      this.currentUser = user
      this.setOibDialog = true
    },
    openAddSellerDialog () {
      this.addSellerDialog = true
    },
    async onOibSet (userId) {
      // Nakon što je OIB postavljen, refresh listu i dodaj u selektovane
      await this.loadUsers()
      if (!this.selectedUserIds.includes(userId)) {
        this.selectedUserIds.push(userId)
      }
    },
    async onSellerAdded (userId) {
      // Čekaj malo da se korisnik propagira u bazu
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Refresh listu da dobijemo novog korisnika
      await this.loadUsers(false)

      // Označi SVE korisnike u listi
      this.selectedUserIds = this.users
        .filter(user => this.hasOibAndPin(user))
        .map(user => user.user.id)
    },
    onDomainCreated (domain) {
      // Proslijedi event gore prema parent-u (SetupWizard)
      this.$emit('domainCreated', domain)
    },
    getSelectedUsers () {
      // Metoda koju wizard poziva za dobijanje liste odabranih korisnika
      return this.selectedUserIds
    },
    hasOibAndPin (user) {
      // Helper za provjeru da li korisnik ima OIB i PIN
      return !!(user.user.oib && user.user.pincode)
    },
    isSuperadmin (user) {
      // Helper za provjeru da li je korisnik superadmin (trenutni korisnik)
      return user.user.id === auth.currentUser.uid
    },
    isUserSelected (userId) {
      // Helper za provjeru da li je korisnik selektovan
      return this.selectedUserIds.includes(userId)
    }
  }
}
</script>

<style scoped>
h3 {
  color: #1577da;
}
</style>
